"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";

type SeparatorChoice = "period" | "colon" | "paren" | "tab" | "custom";

const SEPARATOR_OPTIONS: {
  value: SeparatorChoice;
  labelKey:
    | "separatorPeriod"
    | "separatorColon"
    | "separatorParen"
    | "separatorTab"
    | "separatorCustom";
  separator: string;
}[] = [
  { value: "period", labelKey: "separatorPeriod", separator: ". " },
  { value: "colon", labelKey: "separatorColon", separator: ": " },
  { value: "paren", labelKey: "separatorParen", separator: ") " },
  { value: "tab", labelKey: "separatorTab", separator: "\t" },
  { value: "custom", labelKey: "separatorCustom", separator: "" },
];

interface NumberingOptions {
  start: number;
  separator: string;
  zeroPad: boolean;
  skipBlank: boolean;
}

function countNumberedLines(lines: string[], skipBlank: boolean): number {
  if (!skipBlank) {
    return lines.length;
  }
  return lines.filter((line) => line.trim().length > 0).length;
}

function numberLines(text: string, options: NumberingOptions): string {
  const lines = text.split("\n");
  const numbered = countNumberedLines(lines, options.skipBlank);
  const lastNumber = options.start + Math.max(numbered - 1, 0);
  const width = String(lastNumber).length;
  let counter = options.start;
  return lines
    .map((line) => {
      if (options.skipBlank && line.trim().length === 0) {
        return line;
      }
      const raw = String(counter);
      counter += 1;
      const label = options.zeroPad ? raw.padStart(width, "0") : raw;
      return `${label}${options.separator}${line}`;
    })
    .join("\n");
}

/** English strings. A translated content file overrides these through `ui`. */
export const LINE_NUMBERER_STRINGS = {
  separator: "Separator",
  separatorPeriod: 'Period ". "',
  separatorColon: 'Colon ": "',
  separatorParen: 'Parenthesis ") "',
  separatorTab: "Tab",
  separatorCustom: "Custom",
  startNumber: "Start number",
  customSeparator: "Custom separator",
  zeroPadNumbers: "Zero-pad numbers",
  skipBlankLines: "Skip blank lines",
  inputText: "Input text",
  firstLineSecondLineThird: "First line\nSecond line\nThird line",
  line: "one: line|other: lines",
  addLineNumbers: "Add Line Numbers",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  numberedOutput: "Numbered output",
} as const;

export default function LineNumbererWidget() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [start, setStart] = useState(1);
  const [sepChoice, setSepChoice] = useState<SeparatorChoice>("period");
  const [customSep, setCustomSep] = useState(" - ");
  const [zeroPad, setZeroPad] = useState(false);
  const [skipBlank, setSkipBlank] = useState(false);
  const [copied, setCopied] = useState(false);

  const inputLines = input.length === 0 ? 0 : input.split("\n").length;
  const outputLines = output.length === 0 ? 0 : output.split("\n").length;

  function resolveSeparator(): string {
    if (sepChoice === "custom") {
      return customSep;
    }
    const option = SEPARATOR_OPTIONS.find((o) => o.value === sepChoice);
    return option ? option.separator : ". ";
  }

  function handleStartChange(raw: string) {
    const parsed = parseIntegerInput(raw);
    setStart(parsed ?? 1);
  }

  function handleNumber() {
    if (input.length === 0) {
      return;
    }
    setOutput(
      numberLines(input, {
        start,
        separator: resolveSeparator(),
        zeroPad,
        skipBlank,
      })
    );
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setCopied(false);
  }

  const locale = useToolLocale();
  const strings = useToolUi(LINE_NUMBERER_STRINGS);
  const fmt = useToolNumber();

  return (
    <>
      {/* Options */}
      <section className="space-y-4">
        <div className="space-y-3">
          <p className="font-medium text-foreground text-sm">
            {strings.separator}
          </p>
          <div className="flex flex-wrap gap-3">
            {SEPARATOR_OPTIONS.map((o) => (
              <label
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                key={o.value}
              >
                <input
                  checked={sepChoice === o.value}
                  className="accent-primary"
                  name="separator"
                  onChange={() => setSepChoice(o.value)}
                  type="radio"
                  value={o.value}
                />
                {strings[o.labelKey]}
              </label>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="start-number"
            >
              {strings.startNumber}
            </label>
            <input
              className="w-28 rounded-lg border border-border bg-card p-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="start-number"
              onChange={(e) => handleStartChange(e.target.value)}
              type="number"
              value={start}
            />
          </div>
          {sepChoice === "custom" && (
            <div className="space-y-1">
              <label
                className="block font-medium text-foreground text-sm"
                htmlFor="custom-separator"
              >
                {strings.customSeparator}
              </label>
              <input
                className="w-28 rounded-lg border border-border bg-card p-2 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                id="custom-separator"
                onChange={(e) => setCustomSep(e.target.value)}
                type="text"
                value={customSep}
              />
            </div>
          )}
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
            <input
              checked={zeroPad}
              className="accent-primary"
              onChange={(e) => setZeroPad(e.target.checked)}
              type="checkbox"
            />
            {strings.zeroPadNumbers}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
            <input
              checked={skipBlank}
              className="accent-primary"
              onChange={(e) => setSkipBlank(e.target.checked)}
              type="checkbox"
            />
            {strings.skipBlankLines}
          </label>
        </div>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="number-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="number-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.firstLineSecondLineThird}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {fmt(inputLines)}
          {tPlural(strings.line, locale, inputLines)}
        </p>
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleNumber}
          type="button"
        >
          {strings.addLineNumbers}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="number-output"
          >
            {strings.numberedOutput}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="number-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {fmt(outputLines)}
            {tPlural(strings.line, locale, outputLines)}
          </p>
        </div>
      )}
    </>
  );
}
